import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { z } from 'zod';
import { useLinkAccount } from '../hooks/auth-actions.js';
import { useAuthClient } from '../hooks/auth-provider.js';
import { describeError, mergeFormClasses } from './shared.js';
const passwordSchema = z.string().min(1, 'Enter your password.');
const codeSchema = z.string().trim().min(4, 'Enter the code from your email.');
/**
 * Renders the linking UI Kratos asks for when an OIDC sign-in's email matches an existing
 * local identity. Two paths the user can pick from:
 *
 *   - **Sign in with password** — confirms ownership of the existing account by entering
 *     its password. One round-trip; on success the OIDC credential is merged.
 *   - **Send a one-time code to my email** — Kratos emails a 6-digit code to the duplicate
 *     address (the same address the IdP just told us about, so the user definitionally
 *     controls it). User enters the code; on success the OIDC credential is merged.
 *
 * The form discovers which methods are actually available by calling
 * `client.inspectLinkFlow` on mount — if the existing identity doesn't have a code-method
 * credential, the "send a code" option isn't shown. If neither method is available
 * (unusual), the form displays the inspection failure.
 */
export function LinkAccountForm(props) {
    const classes = mergeFormClasses(props.classNames);
    const client = useAuthClient();
    const { submitPassword, initiateCode, completeCode } = useLinkAccount();
    const [inspection, setInspection] = React.useState(null);
    const [inspectError, setInspectError] = React.useState(null);
    const [mode, setMode] = React.useState('choose');
    const [password, setPassword] = React.useState('');
    const [code, setCode] = React.useState('');
    const [validationError, setValidationError] = React.useState(null);
    React.useEffect(() => {
        let cancelled = false;
        client
            .inspectLinkFlow({ flowId: props.flowId })
            .then((next) => {
            if (cancelled)
                return;
            setInspection(next);
            if (!next.isLinkCredentialsFlow && props.onNotLinkable !== undefined) {
                props.onNotLinkable();
            }
        })
            .catch((reason) => {
            if (cancelled)
                return;
            setInspectError(reason instanceof Error ? reason : new Error(String(reason)));
        });
        return () => {
            cancelled = true;
        };
        // Intentionally only on flowId change; client/onNotLinkable are stable enough at the
        // call site (host apps render once per route load).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.flowId]);
    const submitError = describeError(submitPassword.error ?? completeCode.error ?? initiateCode.error, undefined);
    const handlePassword = async (event) => {
        event.preventDefault();
        if (inspection?.duplicateEmail === null || inspection?.duplicateEmail === undefined)
            return;
        const parsed = passwordSchema.safeParse(password);
        if (!parsed.success) {
            setValidationError(parsed.error.issues[0]?.message ?? 'Enter your password.');
            return;
        }
        setValidationError(null);
        try {
            const result = await submitPassword.run({
                flowId: props.flowId,
                email: inspection.duplicateEmail,
                password: parsed.data,
            });
            props.onSuccess(result);
        }
        catch {
            // Captured by the hook; rendered below.
        }
    };
    const handleSendCode = async () => {
        if (inspection?.duplicateEmail === null || inspection?.duplicateEmail === undefined)
            return;
        setValidationError(null);
        try {
            await initiateCode.run({ flowId: props.flowId, email: inspection.duplicateEmail });
            setMode('code-sent');
        }
        catch {
            // Captured by the hook.
        }
    };
    const handleSubmitCode = async (event) => {
        event.preventDefault();
        if (inspection?.duplicateEmail === null || inspection?.duplicateEmail === undefined)
            return;
        const parsed = codeSchema.safeParse(code);
        if (!parsed.success) {
            setValidationError(parsed.error.issues[0]?.message ?? 'Enter the code.');
            return;
        }
        setValidationError(null);
        try {
            const result = await completeCode.run({
                flowId: props.flowId,
                email: inspection.duplicateEmail,
                code: parsed.data,
            });
            props.onSuccess(result);
        }
        catch {
            // Captured by the hook.
        }
    };
    if (inspectError !== null) {
        return (_jsxs("p", { className: classes.error, role: "alert", children: ["Could not load the linking flow: ", inspectError.message] }));
    }
    if (inspection === null) {
        return _jsx("p", { className: classes.hint, children: "Loading\u2026" });
    }
    if (!inspection.isLinkCredentialsFlow) {
        return (_jsxs("p", { className: classes.error, role: "alert", children: ["This sign-in flow doesn't need linking.", ' ', _jsx("a", { className: classes.link, href: "/login", children: "Return to sign in" }), "."] }));
    }
    const providerLabel = inspection.provider ?? 'the provider';
    const email = inspection.duplicateEmail ?? '';
    const canPassword = inspection.availableMethods.includes('password');
    const canCode = inspection.availableMethods.includes('code');
    return (_jsxs("div", { children: [_jsxs("p", { className: classes.hint, children: ["An account already exists for ", _jsx("strong", { children: email }), ". Confirm it's yours and we'll link ", providerLabel, " as another way to sign in."] }), mode === 'choose' && canPassword ? (_jsxs("form", { className: classes.form, onSubmit: handlePassword, noValidate: true, children: [_jsxs("div", { className: classes.field, children: [_jsxs("label", { className: classes.label, htmlFor: "auth-ui-link-password", children: ["Password for ", email] }), _jsx("input", { id: "auth-ui-link-password", className: classes.input, type: "password", autoComplete: "current-password", required: true, value: password, onChange: (e) => setPassword(e.target.value) }), validationError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: validationError })) : null] }), submitError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: submitError })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: submitPassword.loading, children: submitPassword.loading ? 'Linking…' : `Link with password` }), canCode ? (_jsx("button", { type: "button", className: classes.link, onClick: () => {
                            void handleSendCode();
                        }, disabled: initiateCode.loading, children: initiateCode.loading ? 'Sending code…' : 'Send a one-time code instead' })) : null] })) : null, mode === 'choose' && !canPassword && canCode ? (_jsxs("div", { className: classes.form, children: [_jsx("button", { type: "button", className: classes.button, onClick: () => {
                            void handleSendCode();
                        }, disabled: initiateCode.loading, children: initiateCode.loading ? 'Sending code…' : 'Send me a one-time code' }), submitError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: submitError })) : null] })) : null, mode === 'choose' && !canPassword && !canCode ? (_jsxs("p", { className: classes.error, role: "alert", children: ["The existing account doesn't have password or code login enabled, so we can't link ", providerLabel, " automatically. Contact support."] })) : null, mode === 'code-sent' ? (_jsxs("form", { className: classes.form, onSubmit: handleSubmitCode, noValidate: true, children: [_jsxs("p", { className: classes.hint, children: ["We emailed a one-time code to ", email, "."] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-link-code", children: "Code" }), _jsx("input", { id: "auth-ui-link-code", className: classes.input, inputMode: "numeric", autoComplete: "one-time-code", required: true, value: code, onChange: (e) => setCode(e.target.value) }), validationError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: validationError })) : null] }), submitError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: submitError })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: completeCode.loading, children: completeCode.loading ? 'Linking…' : 'Verify and link' }), _jsx("button", { type: "button", className: classes.link, onClick: () => {
                            setMode('choose');
                            setCode('');
                        }, children: "Use a different option" })] })) : null] }));
}
//# sourceMappingURL=LinkAccountForm.js.map